import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { intakeApi } from '../api/api';
import { Submission } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { user, isVerified } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSubmissions = async (): Promise<void> => {
      try {
        const response = await intakeApi.list();
        setSubmissions(response.data.submissions);
      } catch {
        setError('Failed to load submissions');
      }
      setLoading(false);
    };

    fetchSubmissions();
  }, []);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusClass = (status: string): string => `status-badge status-${status}`;

  // Calculate stats
  const totalSubmissions = submissions.length;
  const pendingCount = submissions.filter(s => s.status === 'submitted' || s.status === 'reviewing').length;
  const approvedCount = submissions.filter(s => s.status === 'approved' || s.status === 'converted').length;
  const totalItems = submissions.reduce((sum, s) => sum + (s.total_items || 0), 0);

  return (
    <div className="dashboard">
      {/* Hero Banner */}
      <div className="dashboard-hero">
        <div className="dashboard-hero-content">
          <div className="dashboard-hero-text">
            <h1>Welcome back, {user?.contactName}!</h1>
            <p>{user?.leagueName}</p>
          </div>
          {isVerified && (
            <Link to="/intake/new" className="btn">
              <span>+</span> New Order Request
            </Link>
          )}
        </div>
      </div>

      {!isVerified && (
        <div className="alert alert-warning">
          <strong>Email Verification Required</strong>
          <p>Please verify your email address to submit order requests. Check your inbox for the verification email.</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-card-icon total">📋</div>
          <div className="stat-card-value">{totalSubmissions}</div>
          <div className="stat-card-label">Total Orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon pending">⏳</div>
          <div className="stat-card-value">{pendingCount}</div>
          <div className="stat-card-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon approved">✓</div>
          <div className="stat-card-value">{approvedCount}</div>
          <div className="stat-card-label">Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon items">👕</div>
          <div className="stat-card-value">{totalItems}</div>
          <div className="stat-card-label">Total Items</div>
        </div>
      </div>

      {/* Submissions Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <div>
            <h2 className="section-title">Your Submissions</h2>
            <p className="section-subtitle">Track the status of your uniform orders</p>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <LoadingSpinner text="Loading submissions..." />
          </div>
        ) : error ? (
          <div className="alert alert-error">{error}</div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>No Submissions Yet</h3>
            <p>You haven't submitted any uniform order requests.</p>
            {isVerified && (
              <Link to="/intake/new" className="btn btn-primary">
                Submit Your First Order Request
              </Link>
            )}
          </div>
        ) : (
          <div className="submissions-table-container">
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>Submission #</th>
                  <th>Season</th>
                  <th>Teams</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id}>
                    <td className="submission-number">{sub.submission_number}</td>
                    <td>{sub.season || '-'}</td>
                    <td>{sub.total_teams}</td>
                    <td>{sub.total_items}</td>
                    <td>
                      <span className={getStatusClass(sub.status)}>
                        {sub.status}
                      </span>
                    </td>
                    <td>{formatDate(sub.created_at)}</td>
                    <td>
                      <Link to={`/submissions/${sub.id}`} className="btn btn-sm btn-secondary">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="dashboard-section">
        <div className="section-header">
          <div>
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Our simple 4-step ordering process</p>
          </div>
        </div>
        <div className="how-it-works">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Submit Request</h4>
              <p>Fill out the intake form with your league's uniform needs</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Staff Review</h4>
              <p>Our team reviews your request and prepares pricing</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Approval</h4>
              <p>You'll receive confirmation and next steps via email</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>Production</h4>
              <p>Your uniforms are produced and delivered to you</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="dashboard-contact">
        <h3>Questions?</h3>
        <p>Contact us at <a href="tel:8308960114">(830) 896-0114</a> or email <a href="mailto:dale@fitness1stsports.com">dale@fitness1stsports.com</a></p>
      </div>
    </div>
  );
};

export default Dashboard;
